import { useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type {
  TenantCreateInput,
  TenantDto,
  TenantUpdateInput,
} from '@cms/models';
import { saasApi } from '../../../lib/api/saas.api';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Toggle } from '@/components/ui/toggle';
import { CommonModal } from '@/components/common/CommonModal';

type TenantFormData = {
  name: string;
  code: string;
  status: string;
  url: string;
  notes: string;
  contactRepresentative: string;
  contactWeb: string;
  contactFacebook: string;
  contactPhone: string;
};

function nameToCode(name: string): string {
  return name
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '-')
    .replace(/[^a-z0-9-_]/g, '');
}

const inputClass =
  'mt-1 block w-full h-9 px-3 py-1.5 text-sm bg-background border border-border rounded-md focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/20 transition-colors';

const textareaClass =
  'mt-1 block w-full px-3 py-2 text-sm bg-background border border-border rounded-md focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/20 transition-colors resize-y';

function buildContact(data: TenantFormData): Record<string, string> {
  const out: Record<string, string> = {};
  if (data.contactRepresentative?.trim()) out.representative = data.contactRepresentative.trim();
  if (data.contactWeb?.trim()) out.web = data.contactWeb.trim();
  if (data.contactFacebook?.trim()) out.facebook = data.contactFacebook.trim();
  if (data.contactPhone?.trim()) out.phone = data.contactPhone.trim();
  return out;
}

export type TenantFormModalMode = 'create' | 'view' | 'edit';

interface TenantFormModalProps {
  readonly open: boolean;
  readonly onClose: () => void;
  readonly mode: TenantFormModalMode;
  /** null when mode is 'create' */
  readonly tenant: TenantDto | null;
  readonly onSuccess?: () => void;
}

export function TenantFormModal({
  open,
  onClose,
  mode,
  tenant,
  onSuccess,
}: TenantFormModalProps) {
  const queryClient = useQueryClient();
  const isCreate = mode === 'create';
  const isView = mode === 'view';

  const {
    register,
    handleSubmit,
    control,
    watch,
    setValue,
    reset,
    formState: { errors },
  } = useForm<TenantFormData>({
    defaultValues: {
      code: '',
      name: '',
      status: 'active',
      url: '',
      notes: '',
      contactRepresentative: '',
      contactWeb: '',
      contactFacebook: '',
      contactPhone: '',
    },
  });

  const nameValue = watch('name');

  useEffect(() => {
    if (tenant) {
      const contact = (tenant as { contact?: Record<string, unknown> | null }).contact;
      reset({
        name: tenant.name ?? '',
        code: tenant.code ?? '',
        status: (tenant.status ?? 'active') as string,
        url: (tenant as { url?: string | null }).url ?? '',
        notes: (tenant as { notes?: string | null }).notes ?? '',
        contactRepresentative: (contact?.representative as string) ?? '',
        contactWeb: (contact?.web as string) ?? '',
        contactFacebook: (contact?.facebook as string) ?? '',
        contactPhone: (contact?.phone as string) ?? '',
      });
    } else if (isCreate) {
      reset({
        code: '',
        name: '',
        status: 'active',
        url: '',
        notes: '',
        contactRepresentative: '',
        contactWeb: '',
        contactFacebook: '',
        contactPhone: '',
      });
    }
  }, [tenant, isCreate, reset]);

  useEffect(() => {
    if (isCreate && nameValue !== undefined) {
      setValue('code', nameToCode(nameValue ?? ''));
    }
  }, [isCreate, nameValue, setValue]);

  const createMutation = useMutation({
    mutationFn: async (data: TenantFormData) => {
      const code = data.code?.trim() || nameToCode(data.name ?? '');
      const status = data.status === 'suspended' ? 'suspended' : 'active';
      const contact = buildContact(data);
      const payload: TenantCreateInput = {
        code: code || 'tenant',
        name: data.name,
        status,
        url: data.url?.trim() || undefined,
        notes: data.notes?.trim() || undefined,
        contact: Object.keys(contact).length > 0 ? contact : undefined,
      };
      return saasApi.createTenant(payload);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['saas-tenants'] });
      onSuccess?.();
      reset();
      onClose();
    },
  });

  const updateMutation = useMutation({
    mutationFn: (data: TenantFormData) => {
      if (!tenant?.id) return Promise.reject(new Error('No tenant'));
      const contact = buildContact(data);
      const payload: TenantUpdateInput = {
        name: data.name,
        status: (data.status as 'active' | 'suspended') ?? 'active',
        url: data.url?.trim() || undefined,
        notes: data.notes?.trim() || undefined,
        contact: Object.keys(contact).length > 0 ? (contact as Record<string, unknown>) : undefined,
      };
      return saasApi.updateTenant(String(tenant.id), payload);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['saas-tenants'] });
      onSuccess?.();
      onClose();
    },
  });

  const onSubmit = async (data: TenantFormData) => {
    if (isView) return;
    try {
      if (isCreate) {
        await createMutation.mutateAsync(data);
      } else {
        await updateMutation.mutateAsync(data);
      }
    } catch (error: unknown) {
      const message =
        error instanceof Error
          ? error.message
          : isCreate
            ? 'Failed to create tenant'
            : 'Failed to update tenant';
      alert(message);
    }
  };

  const title =
    mode === 'create'
      ? 'Create Tenant'
      : mode === 'view'
        ? 'Tenant details'
        : 'Edit tenant';

  const readOnly = isView;

  return (
    <CommonModal
      open={open}
      onClose={onClose}
      title={title}
      titleId="tenant-form-modal-title"
      panelClassName="max-w-2xl max-h-[90vh] overflow-y-auto"
    >
      <form
        onSubmit={handleSubmit(onSubmit)}
        className="space-y-4"
        noValidate
      >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Cột 1: Thông tin tenant */}
            <div className="space-y-4">
              <div className="flex flex-wrap items-end gap-3">
                <div className="flex-1 min-w-[140px]">
                  <label
                    htmlFor="tenant-form-name"
                    className="block text-sm font-medium text-foreground"
                  >
                    Name *
                  </label>
                  <input
                    id="tenant-form-name"
                    type="text"
                    {...register('name', { required: 'Name is required' })}
                    disabled={readOnly}
                    className={cn(
                      inputClass,
                      readOnly && 'cursor-default bg-muted/50'
                    )}
                  />
                  {!readOnly && errors.name && (
                    <p className="mt-1 text-sm text-destructive">
                      {errors.name.message}
                    </p>
                  )}
                </div>
                {readOnly ? (
                  <div>
                    <span className="block text-sm font-medium text-muted-foreground mb-1">
                      Status
                    </span>
                    <span className="inline-flex items-center rounded-md border border-border px-2.5 py-1 text-sm capitalize">
                      {watch('status') || '—'}
                    </span>
                  </div>
                ) : (
                  <Controller
                    name="status"
                    control={control}
                    render={({ field }) => (
                      <Toggle
                        id="tenant-form-status"
                        label="Status"
                        checked={field.value === 'active'}
                        onCheckedChange={checked =>
                          field.onChange(checked ? 'active' : 'suspended')
                        }
                      />
                    )}
                  />
                )}
              </div>

              <div>
                <label
                  htmlFor="tenant-form-code"
                  className="block text-sm font-medium text-foreground"
                >
                  Code {isCreate ? '(auto from name)' : ''}
                </label>
                <input
                  id="tenant-form-code"
                  type="text"
                  readOnly
                  {...register('code')}
                  className={cn(inputClass, 'cursor-default bg-muted/50')}
                />
              </div>

              <div>
                <label
                  htmlFor="tenant-form-url"
                  className="block text-sm font-medium text-foreground"
                >
                  URL
                </label>
                <input
                  id="tenant-form-url"
                  type="url"
                  placeholder="https://..."
                  {...register('url')}
                  disabled={readOnly}
                  className={cn(
                    inputClass,
                    readOnly && 'cursor-default bg-muted/50'
                  )}
                />
              </div>

              <div>
                <label
                  htmlFor="tenant-form-notes"
                  className="block text-sm font-medium text-foreground"
                >
                  Notes
                </label>
                <textarea
                  id="tenant-form-notes"
                  rows={3}
                  {...register('notes')}
                  disabled={readOnly}
                  className={cn(
                    textareaClass,
                    readOnly && 'cursor-default bg-muted/50'
                  )}
                />
              </div>
            </div>

            {/* Cột 2: Contact */}
            <div className="space-y-3 rounded-md border border-border p-4 h-fit">
              <span className="block text-sm font-medium text-foreground">
                Contact
              </span>
              <div className="space-y-3">
                <div>
                  <label
                    htmlFor="tenant-form-contact-representative"
                    className="block text-xs text-muted-foreground"
                  >
                    Representative
                  </label>
                  <input
                    id="tenant-form-contact-representative"
                    type="text"
                    {...register('contactRepresentative')}
                    disabled={readOnly}
                    className={cn(
                      inputClass,
                      readOnly && 'cursor-default bg-muted/50'
                    )}
                  />
                </div>
                <div>
                  <label
                    htmlFor="tenant-form-contact-phone"
                    className="block text-xs text-muted-foreground"
                  >
                    Phone
                  </label>
                  <input
                    id="tenant-form-contact-phone"
                    type="text"
                    {...register('contactPhone')}
                    disabled={readOnly}
                    className={cn(
                      inputClass,
                      readOnly && 'cursor-default bg-muted/50'
                    )}
                  />
                </div>
                <div>
                  <label
                    htmlFor="tenant-form-contact-web"
                    className="block text-xs text-muted-foreground"
                  >
                    Web
                  </label>
                  <input
                    id="tenant-form-contact-web"
                    type="url"
                    placeholder="https://..."
                    {...register('contactWeb')}
                    disabled={readOnly}
                    className={cn(
                      inputClass,
                      readOnly && 'cursor-default bg-muted/50'
                    )}
                  />
                </div>
                <div>
                  <label
                    htmlFor="tenant-form-contact-facebook"
                    className="block text-xs text-muted-foreground"
                  >
                    Facebook
                  </label>
                  <input
                    id="tenant-form-contact-facebook"
                    type="text"
                    placeholder="https://facebook.com/..."
                    {...register('contactFacebook')}
                    disabled={readOnly}
                    className={cn(
                      inputClass,
                      readOnly && 'cursor-default bg-muted/50'
                    )}
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-2 border-t border-border">
            {readOnly ? (
              <Button type="button" variant="outline" onClick={onClose}>
                Close
              </Button>
            ) : (
              <>
                <Button type="button" variant="outline" onClick={onClose}>
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={
                    isCreate
                      ? createMutation.isPending
                      : updateMutation.isPending
                  }
                >
                  {isCreate
                    ? createMutation.isPending
                      ? 'Creating…'
                      : 'Create'
                    : updateMutation.isPending
                      ? 'Saving…'
                      : 'Save'}
                </Button>
              </>
            )}
          </div>
      </form>
    </CommonModal>
  );
}
